/**
 * Box I (spec §5.8), shown before a usable model version: per channel, the scope's Spend over the last 28 days against
 * the 28 before, the main objective and its Cost per Result with its Δ, through D1's OverviewCard and OverviewTable. The
 * Cost per Result names what it is per, through D1's CostFigure (rules-04 E3, T12f), and a rising cost is red (rule
 * 04.3). Plain platform figures, so the box says "ไม่ใช่ MMM". The line under the table names the failing checks, or says
 * the data is ready and the model is not; the admin and the analyst get the import link (plan R28). The rows come built,
 * already cut to D1's Result window (T12b). NULL prints "—", never 0.
 *
 * @module components/budget/InterimCard
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { OverviewCard } from '@/components/overview/OverviewBox';
import { Chip, CostFigure, OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import type { OutcomeMetric } from '@/services/budget/budgetContract';
import type { InterimRow } from '@/services/budget/interim';
import { isCount, unitsName } from '@/services/budget/outcomeUnits';
import type { CheckId } from '@/services/budget/readinessRules';
import type { TFn } from '@/services/channels/channelFormat';
import { deltaOf, deltaTone, TONE_CLASS } from '@/services/looker/lookerDelta';
import { DASH, fmtDelta, fmtMoney, objectiveText } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';

const LINK_CLASS =
  'inline-block rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-neutral-50 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950 print:hidden';

/** ▲ / ▼ with D1's tone: a Spend move is neutral, a rising cost is red. */
const Delta: React.FC<{ field: 'spend' | 'cost_per_result'; now: number | null; before: number | null }> = ({ field, now, before }) => {
  const d = deltaOf(now, before);
  return <span data-delta className={TONE_CLASS[deltaTone(field, d)]}>{fmtDelta(d)}</span>;
};

const columnsOf = (t: TFn, currency: string | null): Array<OverviewColumn<InterimRow>> => [
  {
    id: 'channel', header: t('budget.interim.col.channel'),
    cell: (r) => (
      <span className="inline-flex items-center gap-1.5">
        <PlatformDot platform={r.channel} />
        {platformShortLabel(r.channel)}
        {r.stopped && <Chip text={t('budget.interim.stopped')} />}
      </span>
    ),
  },
  { id: 'spend', header: t('budget.interim.col.spend'), numeric: true, cell: (r) => fmtMoney(r.spend, currency) },
  {
    id: 'spendDelta', header: t('budget.interim.col.spendDelta'), numeric: true,
    cell: (r) => <Delta field="spend" now={r.spend} before={r.spendPrev} />,
  },
  { id: 'objective', header: t('budget.interim.col.objective'), cell: (r) => (r.mainObjective === null ? DASH : objectiveText(r.mainObjective, t)) },
  {
    id: 'cpr', header: t('budget.interim.col.cpr'), numeric: true,
    cell: (r) => <CostFigure row={{ metric: r.metric, costPerResult: r.cpr }} currency={currency} />,
  },
  {
    id: 'cprDelta', header: t('budget.interim.col.cprDelta'), numeric: true,
    cell: (r) => <Delta field="cost_per_result" now={r.cpr} before={r.cprPrev} />,
  },
];

/** The proposal's short names for the seven checks. Literal keys, so the locale integrity test sees them. */
export const checkName = (id: CheckId, t: TFn): string => {
  if (id === 'history') return t('budget.checkName.history');
  if (id === 'overlap') return t('budget.checkName.overlap');
  if (id === 'channels') return t('budget.checkName.channels');
  if (id === 'continuity') return t('budget.checkName.continuity');
  if (id === 'outcome') return t('budget.checkName.outcome');
  if (id === 'scope') return t('budget.checkName.scope');
  return t('budget.checkName.currency');
};

/** Why there is no MMM yet: the failing checks, or a ready dataset waiting for its model. */
const Status: React.FC<{ failing: readonly CheckId[]; readyNoVersion: boolean }> = ({ failing, readyNoVersion }) => {
  const { t } = useTranslation();
  const missing = failing.map((id) => checkName(id, t)).join(' · ');
  const head = readyNoVersion
    ? <b>{t('budget.interim.readyNoVersion')}</b>
    : failing.length > 0 && <><b>{t('budget.interim.notEnough')}</b> {t('budget.interim.missing', { checks: missing })}</>;
  return (
    <p data-interim-status className="mt-3 rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-800">
      {head}
      {head && ' · '}
      {t('budget.interim.answers')}
    </p>
  );
};

interface Props {
  /** interimRowsOf(…), built by the page (T12b). */
  rows: readonly InterimRow[];
  failing: readonly CheckId[];
  readyNoVersion: boolean;
  canImport: boolean;
  scopeConfirmed: boolean;
  /** The scope's one currency (spec §5.9); null prints every money cell as "—". */
  currency: string | null;
  /** The config's outcome, or revenue before a confirm (R3): the import link names a count's units (T18c). */
  metric: OutcomeMetric;
}

export const InterimCard: React.FC<Props> = ({ rows, failing, readyNoVersion, canImport, scopeConfirmed, currency, metric }) => {
  const { t } = useTranslation();
  return (
    <OverviewCard id="budget-i" title={t('budget.interim.title')}>
      <p className="-mt-2 mb-3 text-xs text-neutral-700">
        {t('budget.interim.subtitle')} · <b data-not-mmm className="text-warning-700">{t('budget.interim.notMmm')}</b>
      </p>
      {rows.length === 0
        ? <BoxMessage text={t('looker.tileEmpty')} />
        : <OverviewTable columns={columnsOf(t, currency)} rows={rows} rowKey={(r) => r.channel} />}
      <Status failing={failing} readyNoVersion={readyNoVersion} />
      {!scopeConfirmed && <p data-interim-all-accounts className="mt-2 text-xs text-neutral-700">{t('budget.interim.allAccounts')}</p>}
      {canImport && (
        <div className="mt-3">
          <Link to="/settings/data" className={LINK_CLASS}>
            {isCount(metric) ? t('budget.interim.importCount', { units: unitsName(metric, t) }) : t('budget.interim.import')}
          </Link>
        </div>
      )}
    </OverviewCard>
  );
};
